import {
  computeSheet,
  FINANCIAL_AMOUNTS,
  formatMoney,
  formatNumber,
  formatPercent,
  formatYearMonth,
  fromCents,
  type MonthAmount,
  type Resource,
  type Scenario,
  type Series,
} from '@pmai/contracts';
import {
  CellInput,
  Icon,
  IconButton,
  Sheet,
  SheetBody,
  type SheetCellTone,
  SheetHead,
  SheetRow,
  type SheetRowKind,
  SheetTd,
  SheetTh,
} from '@pmai/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type EditRow,
  type EditSheet,
  parseAmount,
  parseHours,
  parsePercent,
  setAdditionalPct,
  setAmount,
  setHours,
  setRate,
  setRowName,
  toggleLock,
} from './sheet-edit';

const money = (cents: number) => formatMoney(fromCents(cents));
const signTone = (cents: number): SheetCellTone =>
  cents < 0 ? 'negative' : cents > 0 ? 'positive' : 'computed';
/** Number shown in an editable cell — no thousands separator, comma decimals. */
const plain = (value: string | number | null) => String(Number(value ?? 0)).replace('.', ',');

/**
 * One Plan or Fact table (FR-TS-001…012): resources with rate and hours per month, totals,
 * additional %, licence costs, total costs, financial rows, profit and margin. Without
 * `finance.view` only the hours are shown (ADR 0004).
 */
export function TimesheetGrid({
  scenario,
  sheet,
  editable,
  finance,
  resources,
  onChange,
  onRemoveRow,
}: {
  scenario: Scenario;
  sheet: EditSheet;
  editable: boolean;
  finance: boolean;
  resources: readonly Resource[];
  onChange: (fn: (sheet: EditSheet) => EditSheet) => void;
  onRemoveRow: (row: EditRow) => void;
}) {
  const { t } = useTranslation();
  const f = computeSheet(sheet);
  const months = sheet.months.map((m) => ({ ...m, label: formatYearMonth(m) }));
  const span = finance ? 2 : 1;
  const listId = `timesheet-resources-${scenario}`;
  const rowName = (r: EditRow, i: number) => r.name || `#${i + 1}`;
  const commit =
    <T,>(parse: (text: string) => T | null, apply: (value: T) => (s: EditSheet) => EditSheet) =>
    (text: string) => {
      const value = parse(text);
      if (value !== null) onChange(apply(value));
    };

  /** A row with one value per month (hours or amount column) and a total. */
  const line = (
    kind: SheetRowKind,
    label: ReactNode,
    rate: ReactNode,
    cell: (i: number) => { hours?: ReactNode; amount?: ReactNode; tone?: SheetCellTone },
    total: { hours?: ReactNode; amount?: ReactNode; tone?: SheetCellTone },
    key: string,
  ) => (
    <SheetRow kind={kind} key={key} rowKey={key}>
      <SheetTd sticky align="left">
        {label}
      </SheetTd>
      {finance && <SheetTd>{rate}</SheetTd>}
      {months.map((m, i) => {
        const c = cell(i);
        const locked = m.locked ? 'locked' : undefined;
        return [
          <SheetTd key={`${key}-h${i}`} data-col={`${i}-h`} tone={locked ?? 'computed'}>
            {c.hours}
          </SheetTd>,
          finance && (
            <SheetTd key={`${key}-a${i}`} data-col={`${i}-a`} tone={locked ?? c.tone ?? 'computed'}>
              {c.amount}
            </SheetTd>
          ),
        ];
      })}
      <SheetTd tone="total" data-col="total-h">
        {total.hours}
      </SheetTd>
      {finance && (
        <SheetTd tone={total.tone ?? 'total'} data-col="total-a">
          {total.amount}
        </SheetTd>
      )}
    </SheetRow>
  );

  const moneyLine = (key: string, label: string, s: Series, tone?: (c: number) => SheetCellTone) =>
    line(
      'total',
      label,
      null,
      (i) => ({ amount: money(s.values[i]!), tone: tone?.(s.values[i]!) }),
      { amount: money(s.total), tone: tone?.(s.total) },
      key,
    );

  const amountLine = (field: MonthAmount, label: string) =>
    line(
      'plain',
      label,
      null,
      (i) => ({
        amount: (
          <CellInput
            label={`${label} — ${months[i]!.label}`}
            value={plain(sheet.months[i]![field])}
            readOnly={!editable || months[i]!.locked}
            onCommit={commit(parseAmount, (v) => (s) => setAmount(s, field, i, v))}
          />
        ),
      }),
      { amount: money(f.amounts[field].total) },
      field,
    );

  return (
    <>
      <datalist id={listId}>
        {resources.map((r) => (
          <option key={r.id} value={r.name} />
        ))}
      </datalist>
      <Sheet label={t(`timesheets.${scenario}`)}>
        <SheetHead>
          <SheetRow>
            <SheetTh sticky rowSpan={2}>
              {t('timesheets.description')}
            </SheetTh>
            {finance && <SheetTh rowSpan={2}>{t('timesheets.rate')}</SheetTh>}
            {months.map((m, i) => (
              <SheetTh key={m.label} colSpan={span} tone={m.locked ? 'locked' : 'plain'}>
                <span className="inline-flex items-center gap-1">
                  {m.label}
                  {editable ? (
                    <IconButton
                      icon={m.locked ? 'lock' : 'lock-open'}
                      label={t(m.locked ? 'timesheets.unlock' : 'timesheets.lock', { month: m.label })}
                      aria-pressed={m.locked}
                      onClick={() => onChange((s) => toggleLock(s, i))}
                    />
                  ) : (
                    m.locked && (
                      <span role="img" title={t('timesheets.locked')} aria-label={t('timesheets.locked')}>
                        <Icon name="lock" className="size-3.5" />
                      </span>
                    )
                  )}
                </span>
              </SheetTh>
            ))}
            <SheetTh colSpan={span} tone="total">
              {t('timesheets.total')}
            </SheetTh>
          </SheetRow>
          <SheetRow>
            {[...months.map((m) => m.label), 'total'].map((k) => [
              <SheetTh key={`${k}-h`}>{t('timesheets.hours')}</SheetTh>,
              finance && <SheetTh key={`${k}-a`}>{t('timesheets.amount')}</SheetTh>,
            ])}
          </SheetRow>
        </SheetHead>
        <SheetBody>
          {sheet.rows.map((r, ri) =>
            line(
              'plain',
              <span className="flex items-center gap-1">
                <CellInput
                  label={`${t('timesheets.resourceName')} ${ri + 1}`}
                  value={r.name}
                  align="left"
                  width="w-44"
                  placeholder={t('timesheets.resourceName')}
                  list={listId}
                  readOnly={!editable}
                  onCommit={(text) => onChange((s) => setRowName(s, r.key, text, resources))}
                />
                {editable && (
                  <IconButton
                    icon="x"
                    label={t('timesheets.removeResource', { name: rowName(r, ri) })}
                    onClick={() => onRemoveRow(r)}
                  />
                )}
              </span>,
              <CellInput
                label={t('timesheets.rowRate', { row: rowName(r, ri) })}
                value={plain(r.rate)}
                width="w-16"
                readOnly={!editable}
                onCommit={commit(parseAmount, (v) => (s) => setRate(s, r.key, v))}
              />,
              (i) => ({
                hours: (
                  <CellInput
                    label={t('timesheets.cellHours', { row: rowName(r, ri), month: months[i]!.label })}
                    value={plain(r.hours[i] ?? 0)}
                    width="w-14"
                    readOnly={!editable || months[i]!.locked}
                    onCommit={commit(parseHours, (v) => (s) => setHours(s, r.key, i, v))}
                  />
                ),
                amount: money(f.rows[ri]!.amounts.values[i]!),
              }),
              { hours: formatNumber(f.rows[ri]!.hours.total), amount: money(f.rows[ri]!.amounts.total) },
              `resource-${ri + 1}`,
            ),
          )}
          {line(
            'total',
            t('timesheets.totalExpenses'),
            null,
            (i) => ({
              hours: formatNumber(f.expenses.hours.values[i]!),
              amount: money(f.expenses.amounts.values[i]!),
            }),
            { hours: formatNumber(f.expenses.hours.total), amount: money(f.expenses.amounts.total) },
            'expenses',
          )}
          {finance && (
            <>
              {line(
                'subtle',
                t('timesheets.additional'),
                <CellInput
                  label={t('timesheets.additional')}
                  value={plain(sheet.additionalPct)}
                  width="w-16"
                  readOnly={!editable}
                  onCommit={commit(parsePercent, (v) => (s) => setAdditionalPct(s, v))}
                />,
                (i) => ({ amount: money(f.additional.values[i]!) }),
                { amount: money(f.additional.total) },
                'additional',
              )}
              {amountLine('licenceCosts', t('timesheets.licence'))}
              {moneyLine('costs', t('timesheets.totalCosts'), f.costs)}
              {FINANCIAL_AMOUNTS.map((field) => amountLine(field, t(`timesheets.amounts.${field}`)))}
              {moneyLine('profit', t('timesheets.profit'), f.profit, signTone)}
              {line(
                'total',
                t('timesheets.margin'),
                null,
                (i) => ({ amount: formatPercent(f.margin.values[i]!) }),
                { amount: formatPercent(f.margin.total) },
                'margin',
              )}
            </>
          )}
        </SheetBody>
      </Sheet>
    </>
  );
}
